import { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../services/supabase';
import { AdminPageId, getAdminPageConfig } from '../config/adminPageConfig';
import { GLOBAL_ADMIN_LOCATION } from '../constants/adminSettings';
import { useUserRole } from '../contexts/UserRoleContext';

export interface AdminChartState {
  id: string;
  enabled: boolean;
  order: number;
  visibleRoles: string[] | null;
}

export interface AdminSectionState {
  id: string;
  enabled: boolean;
  order: number;
  charts: AdminChartState[];
}

export interface AdminPageSettingsState {
  sections: AdminSectionState[];
}

const buildDefaultSettings = (pageId: AdminPageId): AdminPageSettingsState => {
  const config = getAdminPageConfig(pageId);
  if (!config) {
    return { sections: [] };
  }

  return {
    sections: config.sections.map((section, sectionIndex) => ({
      id: section.id,
      enabled: true,
      order: sectionIndex,
      charts: section.charts.map((chart, chartIndex) => ({
        id: chart.id,
        enabled: true,
        order: chartIndex,
        visibleRoles: null
      }))
    }))
  };
};

export const useAdminPageSettings = (pageId: AdminPageId, locationName?: string | null) => {
  const { hasAnyRole } = useUserRole();
  const defaultSettings = useMemo(() => buildDefaultSettings(pageId), [pageId]);
  const [settings, setSettings] = useState<AdminPageSettingsState>(defaultSettings);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSettings(defaultSettings);
  }, [defaultSettings]);

  useEffect(() => {
    if (!locationName) return;
    const loadSettings = async () => {
      setLoading(true);
      const fetchChartRows = async (targetLocation: string) => {
        const withRoles = await supabaseClient
          .from('admin_page_charts')
          .select('section_id, chart_id, is_enabled, sort_order, visible_roles')
          .eq('location_name', targetLocation)
          .eq('page_id', pageId);
        if (!withRoles.error) return withRoles;
        if (!/visible_roles/i.test(withRoles.error.message || '')) return withRoles;
        const withoutRoles = await supabaseClient
          .from('admin_page_charts')
          .select('section_id, chart_id, is_enabled, sort_order')
          .eq('location_name', targetLocation)
          .eq('page_id', pageId);
        if (withoutRoles.error) return withoutRoles as any;
        return {
          data: (withoutRoles.data || []).map((row: any) => ({ ...row, visible_roles: null })),
          error: null
        } as any;
      };

      const [localSectionsRes, globalSectionsRes, localChartsRes, globalChartsRes] = await Promise.all([
        supabaseClient
          .from('admin_page_sections')
          .select('section_id, is_enabled, sort_order')
          .eq('location_name', locationName)
          .eq('page_id', pageId),
        supabaseClient
          .from('admin_page_sections')
          .select('section_id, is_enabled, sort_order')
          .eq('location_name', GLOBAL_ADMIN_LOCATION)
          .eq('page_id', pageId),
        fetchChartRows(locationName),
        fetchChartRows(GLOBAL_ADMIN_LOCATION)
      ]);

      const sectionError = localSectionsRes.error || globalSectionsRes.error;
      const chartError = localChartsRes.error || globalChartsRes.error;
      if (sectionError || chartError) {
        console.error('Error loading admin page settings:', sectionError || chartError);
        setLoading(false);
        return;
      }
      const localSectionRows = localSectionsRes.data || [];
      const globalSectionRows = globalSectionsRes.data || [];
      const localChartRows = localChartsRes.data || [];
      const globalChartRows = globalChartsRes.data || [];

      const nextSettings: AdminPageSettingsState = {
        sections: defaultSettings.sections.map((section) => {
          const sectionRow =
            localSectionRows.find((row) => row.section_id === section.id) ||
            globalSectionRows.find((row) => row.section_id === section.id);
          return {
            ...section,
            enabled: sectionRow ? sectionRow.is_enabled : section.enabled,
            order: sectionRow && typeof sectionRow.sort_order === 'number' ? sectionRow.sort_order : section.order,
            charts: section.charts.map((chart) => {
              const chartRow =
                localChartRows.find((row: any) => row.section_id === section.id && row.chart_id === chart.id) ||
                globalChartRows.find((row: any) => row.section_id === section.id && row.chart_id === chart.id);
              return {
                ...chart,
                enabled: chartRow ? chartRow.is_enabled : chart.enabled,
                order: chartRow && typeof chartRow.sort_order === 'number' ? chartRow.sort_order : chart.order,
                visibleRoles: Array.isArray((chartRow as any)?.visible_roles) ? (chartRow as any).visible_roles : null
              };
            })
          };
        })
      };

      setSettings(nextSettings);
      setLoading(false);
    };

    loadSettings();
  }, [pageId, locationName, defaultSettings]);

  const getSectionState = (sectionId: string) => settings.sections.find((section) => section.id === sectionId);
  const getChartState = (sectionId: string, chartId: string) =>
    getSectionState(sectionId)?.charts.find((chart) => chart.id === chartId);

  const isSectionEnabled = (sectionId: string) => getSectionState(sectionId)?.enabled ?? true;
  const isChartEnabled = (sectionId: string, chartId: string) => {
    const chart = getChartState(sectionId, chartId);
    const enabled = chart?.enabled ?? true;
    if (!enabled) return false;
    const roles = chart?.visibleRoles;
    if (roles === null || roles === undefined) return true;
    if (roles.length === 0) return false;
    if (roles.includes('public')) return true;
    const allowedRoles = roles.filter((role) =>
      ['super_admin', 'developer', 'academic', 'local_govt_admin', 'local_govt_admin_pending', 'user'].includes(role)
    ) as Array<'super_admin' | 'developer' | 'academic' | 'local_govt_admin' | 'local_govt_admin_pending' | 'user'>;
    return allowedRoles.length > 0 ? hasAnyRole(allowedRoles) : false;
  };

  const getSectionOrder = (sectionId: string) => getSectionState(sectionId)?.order ?? 999;
  const getChartOrder = (sectionId: string, chartId: string) => getChartState(sectionId, chartId)?.order ?? 0;

  const getSectionsInOrder = () =>
    [...settings.sections].sort((a, b) => a.order - b.order);

  const getChartsInOrder = (sectionId: string) => {
    const section = getSectionState(sectionId);
    if (!section) return [];
    return [...section.charts].sort((a, b) => a.order - b.order);
  };

  return {
    settings,
    loading,
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder,
    getSectionsInOrder,
    getChartsInOrder
  };
};
